import React from "react";
import "./Skeleton.css";

export function Skeleton({
  width,
  height,
  circle = false,
  rounded = false,
  className = "",
  style = {},
}) {
  const classes = [
    "skeleton",
    circle && "skeleton-circle",
    rounded && "skeleton-rounded",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={classes}
      style={{
        width: width || "100%",
        height: height || "16px",
        ...style,
      }}
      aria-hidden="true"
    />
  );
}

export function CarCardSkeleton() {
  return (
    <div className="skeleton-car-card">
      <Skeleton className="skeleton-car-image" height="210px" />
      <div className="skeleton-car-body">
        <Skeleton height="18px" width="78%" rounded />
        <div className="skeleton-chip-row">
          <Skeleton height="24px" width="52px" rounded />
          <Skeleton height="24px" width="48px" rounded />
          <Skeleton height="24px" width="56px" rounded />
        </div>
        <div className="skeleton-car-price-row">
          <Skeleton height="14px" width="64px" rounded />
          <Skeleton height="22px" width="88px" rounded />
        </div>
        <Skeleton height="12px" width="72%" />
        <div className="skeleton-chip-row skeleton-car-actions">
          <Skeleton height="38px" rounded style={{ flex: 1 }} />
          <Skeleton height="38px" rounded style={{ flex: 1 }} />
        </div>
      </div>
    </div>
  );
}

export function CarGridSkeleton({ count = 6 }) {
  return (
    <div className="skeleton-car-grid">
      {Array.from({ length: count }).map((_, i) => (
        <CarCardSkeleton key={i} />
      ))}
    </div>
  );
}

export function SectionTitleSkeleton() {
  return (
    <div className="skeleton-section-title-wrap">
      <Skeleton className="skeleton-section-title" height="28px" width="240px" />
    </div>
  );
}

export function HeroSkeleton() {
  return (
    <div className="skeleton-hero-wrap">
      <div className="skeleton-hero-banner">
        <Skeleton height="18px" width="180px" rounded style={{ marginBottom: 12 }} />
        <Skeleton height="42px" width="min(520px, 80%)" rounded />
        <Skeleton height="42px" width="min(400px, 65%)" rounded style={{ marginTop: 8 }} />
      </div>
      <div className="skeleton-hero-box">
        <div className="skeleton-chip-row skeleton-hero-btns">
          <Skeleton height="44px" width="120px" rounded />
          <Skeleton height="44px" width="120px" rounded />
          <Skeleton height="44px" width="140px" rounded />
        </div>
        <div className="skeleton-chip-row">
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} height="72px" width="72px" rounded />
          ))}
        </div>
        <div className="skeleton-fuel-row">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height="36px" rounded style={{ flex: 1, minWidth: 80 }} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function HomePageSkeleton() {
  return (
    <div className="skeleton-home-page skeleton-home-page--content-only">
      <div className="skeleton-home-sections">
        <SectionTitleSkeleton />
        <CarGridSkeleton count={8} />
        <SectionTitleSkeleton />
        <CarGridSkeleton count={8} />
        <SectionTitleSkeleton />
        <Skeleton height="200px" rounded className="skeleton-banner-block" />
      </div>
    </div>
  );
}

export function CarDetailsSkeleton() {
  return (
    <div className="skeleton-details-page">
      <Skeleton height="52px" className="skeleton-details-topbar" />
      <div className="skeleton-details-shell">
        <div className="skeleton-details-main">
          <Skeleton height="420px" rounded className="skeleton-details-gallery" />
          <div className="skeleton-details-thumbs">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height="58px" width="76px" rounded />
            ))}
          </div>
          <Skeleton height="48px" rounded style={{ marginTop: 16 }} />
          <div className="skeleton-details-panel">
            <Skeleton height="80px" rounded />
            <Skeleton height="80px" rounded />
            <Skeleton height="80px" rounded />
            <Skeleton height="120px" rounded />
          </div>
        </div>
        <aside className="skeleton-details-sidebar">
          <Skeleton height="22px" width="80px" rounded />
          <Skeleton height="28px" width="90%" rounded />
          <Skeleton height="36px" width="60%" rounded />
          <Skeleton height="64px" rounded />
          <div className="skeleton-details-spec-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} height="40px" rounded />
            ))}
          </div>
          <Skeleton height="56px" rounded />
          <Skeleton height="48px" rounded />
          <Skeleton height="48px" rounded />
        </aside>
      </div>
    </div>
  );
}

export function CarListGridSkeleton({ count = 6 }) {
  return (
    <div className="skeleton-list-car-grid">
      {Array.from({ length: count }).map((_, i) => (
        <CarCardSkeleton key={i} />
      ))}
    </div>
  );
}

export function FilterSidebarSkeleton() {
  const chipSections = [
    { titleWidth: "55px", chips: [72, 88, 64, 96, 70] },
    { titleWidth: "50px", chips: [80, 92, 76, 84], scrollBox: true },
    { titleWidth: "70px", chips: [68, 74, 82, 66] },
    { titleWidth: "35px", chips: [58, 62, 70, 64] },
  ];

  const rangeSections = ["Model Year", "Price", "Kilometer Driven"];

  return (
    <aside className="skeleton-filter-sidebar">
      {chipSections.map((section, index) => (
        <div key={index} className="skeleton-filter-section">
          <Skeleton height="14px" width={section.titleWidth} rounded />
          <div
            className={`skeleton-filter-chips ${
              section.scrollBox ? "skeleton-filter-chips--scroll" : ""
            }`}
          >
            {section.chips.map((width, i) => (
              <Skeleton
                key={i}
                height="32px"
                width={`${width}px`}
                rounded
                className="skeleton-filter-chip"
              />
            ))}
          </div>
        </div>
      ))}

      {rangeSections.map((_, index) => (
        <div key={`range-${index}`} className="skeleton-filter-section">
          <Skeleton height="14px" width="90px" rounded />
          <div className="skeleton-filter-range">
            <Skeleton height="34px" rounded className="skeleton-filter-range-input" />
            <Skeleton height="34px" rounded className="skeleton-filter-range-input" />
            <Skeleton height="34px" width="44px" rounded />
          </div>
        </div>
      ))}

      <Skeleton height="42px" rounded className="skeleton-filter-clear" />
    </aside>
  );
}

export function CarListPageSkeleton({ count = 6 }) {
  return (
    <div className="skeleton-list-page">
      <Skeleton height="48px" rounded className="skeleton-search-bar" />
      <Skeleton height="16px" width="200px" className="skeleton-list-meta" />
      <div className="skeleton-list-layout">
        <FilterSidebarSkeleton />
        <div className="skeleton-list-main">
          <CarListGridSkeleton count={count} />
          <div className="skeleton-list-pagination">
            <Skeleton height="36px" width="36px" rounded />
            <Skeleton height="36px" width="36px" rounded />
            <Skeleton height="36px" width="36px" rounded />
            <Skeleton height="36px" width="36px" rounded />
            <Skeleton height="36px" width="36px" rounded />
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProfilePageSkeleton() {
  return (
    <div className="skeleton-profile">
      <aside className="skeleton-profile-sidebar">
        <Skeleton circle width="80px" height="80px" />
        <Skeleton height="18px" width="120px" />
        <Skeleton height="14px" width="160px" />
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} height="40px" width="100%" rounded />
        ))}
      </aside>
      <main className="skeleton-profile-main">
        <div className="skeleton-profile-card">
          <Skeleton height="24px" width="200px" style={{ marginBottom: 8 }} />
          <Skeleton height="14px" width="280px" style={{ marginBottom: 24 }} />
          <div className="skeleton-form-grid">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="skeleton-field" />
            ))}
          </div>
          <Skeleton height="48px" width="160px" rounded style={{ marginTop: 24 }} />
        </div>
      </main>
    </div>
  );
}

export function FormPageSkeleton() {
  return (
    <div className="skeleton-form-page">
      <div className="skeleton-profile-card">
        <Skeleton height="28px" width="220px" style={{ marginBottom: 8 }} />
        <Skeleton height="14px" width="300px" style={{ marginBottom: 24 }} />
        <div className="skeleton-form-grid">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="skeleton-field" />
          ))}
        </div>
        <Skeleton height="360px" rounded style={{ marginTop: 20 }} className="skeleton-map-block" />
      </div>
    </div>
  );
}

export function ContentPageSkeleton() {
  return (
    <div className="skeleton-content-page">
      <Skeleton height="220px" className="skeleton-content-hero" />
      <div className="skeleton-content-body">
        <Skeleton height="28px" width="220px" rounded style={{ marginBottom: 12 }} />
        <Skeleton height="14px" width="320px" style={{ marginBottom: 24 }} />
        <div className="skeleton-form-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="skeleton-field" />
          ))}
        </div>
      </div>
    </div>
  );
}

export function PageSkeleton({ type = "default" }) {
  switch (type) {
    case "home":
      return <HomePageSkeleton />;
    case "cars":
      return <CarListPageSkeleton count={6} />;
    case "details":
      return <CarDetailsSkeleton />;
    case "profile":
      return <ProfilePageSkeleton />;
    case "form":
      return <FormPageSkeleton />;
    case "content":
      return <ContentPageSkeleton />;
    default:
      return (
        <div className="skeleton-default-page">
          <Skeleton height="32px" width="240px" rounded style={{ marginBottom: 16 }} />
          <Skeleton height="16px" width="320px" style={{ marginBottom: 32 }} />
          <CarGridSkeleton count={3} />
        </div>
      );
  }
}
